import {
    type ChatMessage,
    convertThreadMessagesToChatMessages,
    listAllMastraThreadMessages,
} from '@fruitful/llm-service-shared';
import { useQuery } from '@tanstack/react-query';

import { captureException } from '../../logger/Logger';
import { useMastraClient } from '../providers/MastraClient';
import {
    buildTranscriptWidgetSources,
    type TranscriptWidgetSources,
} from '../utils/transcriptWidgetSources';

export interface MastraThreadTranscript {
    messages: ChatMessage[];
    widgetSources: Map<string, TranscriptWidgetSources>;
}

export const useGetMastraThreadTranscript = ({
    threadId,
    agentId,
    refetchInterval,
}: {
    threadId?: string;
    agentId?: string;
    refetchInterval?: number | false;
}) => {
    const mastraClient = useMastraClient();

    return useQuery({
        queryKey: ['mastraThreadTranscript', threadId, agentId],
        refetchInterval,
        queryFn: async (): Promise<MastraThreadTranscript> => {
            if (threadId == null || agentId == null) {
                return { messages: [], widgetSources: new Map() };
            }

            try {
                const thread = mastraClient.getMemoryThread({
                    threadId,
                    agentId,
                });
                const messages = await listAllMastraThreadMessages(
                    async (params) => await thread.listMessages(params),
                );
                return {
                    messages: convertThreadMessagesToChatMessages(messages),
                    widgetSources: buildTranscriptWidgetSources(messages),
                };
            } catch (err) {
                captureException(err);
                throw err;
            }
        },
        enabled: threadId != null && agentId != null,
    });
};
